import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { Building2, Lock, Mail, LogIn } from "lucide-react";
import { signIn, getMe } from "@/integrations/api/auth";
import { useAuthStore } from "@/store/useAuthStore";

export const Route = createFileRoute("/vendor-login")({
  ssr: false,
  component: VendorLoginPage,
});

function VendorLoginPage() {
  const navigate = useNavigate();
  const setAuth = useAuthStore((s) => s.setAuth);
  
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    try {
      // 1. Sign in via Django API
      await signIn(email, password);
      
      // 2. Fetch user profile
      const me = await getMe();
      setAuth(me);

      // 3. Verify vendor role
      const isVendor = me.roles.includes("vendor" as any);
      if (!isVendor) {
        useAuthStore.getState().clearAuth();
        throw new Error("This login is not a vendor account. Please use the staff login page.");
      }
      
      navigate({ to: "/vendor" });
    } catch (e: unknown) {
      setErr(e instanceof Error ? e.message : "Sign-in failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="grid min-h-screen place-items-center bg-slate-50 px-4 py-10">
      <div className="w-full max-w-md rounded-2xl border border-border bg-card p-8 shadow-lg">
        <div className="flex items-center gap-3">
          <span className="grid h-11 w-11 place-items-center rounded-xl bg-primary text-primary-foreground">
            <Building2 className="h-5 w-5" />
          </span>
          <div>
            <p className="text-lg font-bold">Vendor Portal</p>
            <p className="text-xs text-muted-foreground">Sign in to manage RFQs, POs and quotations</p>
          </div>
        </div>

        <form className="mt-8 space-y-4" onSubmit={submit}>
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-muted-foreground">Email</label>
            <div className="relative mt-1.5">
              <Mail className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <input
                type="email" required autoComplete="email"
                value={email} onChange={(e) => setEmail(e.target.value)}
                className="h-11 w-full rounded-lg border border-border bg-background pl-9 pr-3 text-sm outline-none focus:border-primary"
              />
            </div>
          </div>
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-muted-foreground">Password</label>
            <div className="relative mt-1.5">
              <Lock className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <input
                type="password" required autoComplete="current-password"
                value={password} onChange={(e) => setPassword(e.target.value)}
                className="h-11 w-full rounded-lg border border-border bg-background pl-9 pr-3 text-sm outline-none focus:border-primary"
              />
            </div>
          </div>
          {err && <p className="text-sm text-destructive">{err}</p>}
          <button
            type="submit" disabled={busy}
            className="flex h-11 w-full items-center justify-center gap-2 rounded-lg bg-primary text-sm font-semibold text-primary-foreground disabled:opacity-60"
          >
            <LogIn className="h-4 w-4" /> {busy ? "Signing in…" : "Sign in"}
          </button>
        </form>

        <p className="mt-6 text-xs text-muted-foreground">
          Staff member? <Link to="/auth" className="text-primary underline">Use staff login</Link>
        </p>
      </div>
    </div>
  );
}
